import { FC, useMemo } from "react";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { Filter, GroupFilterEnum } from "constants/filters";
import { ITeam } from "store/entities/teams";

import { createRemovedTeamOption } from "../helpers";
import { IFilterUpdate } from "../interfaces";
import { withFilterBase } from "../withFilterBase";

type IProps = {
  filterValues: string[];
  groups: ITeam[];
  onChange(filterUpdate: IFilterUpdate<string[] | null>): void;
  multiple?: boolean;
};

const getTeamPickerListItem = (team: ITeam): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    listItemBody: <TeamPickerListItem name={team.name} pickerType="multi" isDefault={team.isDefault} />,
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} pickerType="multi" avatarOnly />,
  },
});

const GroupFilter: FC<IProps> = props => {
  const { groups, filterValues, onChange, multiple } = props;
  const handleChange = (selectedItems: IPickerListItem[] | null): void => {
    if (!selectedItems || selectedItems.length === 0) {
      onChange({ name: Filter.Group, value: null });
      return;
    }
    /**
     * Due to the poor Picker API we need to calculate the currently selected
     * item, we have to handle cases:
     * - We are selecting MyTeams and other groups are selected - we need to
     * remove other groups
     * - We are selecting other groups and MyTeams is selected - we need to
     * remove MyTeams
     */
    const currentlySelectedItem =
      (selectedItems?.length || 0) > (filterValues?.length || 0)
        ? selectedItems?.filter(item => !filterValues?.includes(item.key))[0]
        : selectedItems?.filter(item => !selectedItems?.map(i => i.key).includes(item.key))[0];

    const isMyTeamsCurrentlySelected = currentlySelectedItem?.key === GroupFilterEnum.MyTeams;

    const isMyTeamsAlreadySelected = filterValues?.includes(GroupFilterEnum.MyTeams);

    if (isMyTeamsCurrentlySelected) {
      // If MyTeams is selected, we need to remove other groups
      onChange({ name: Filter.Group, value: [GroupFilterEnum.MyTeams] });
    } else if (isMyTeamsAlreadySelected) {
      // If we are selecting other groups and MyTeams is selected, we need to remove MyTeams
      onChange({
        name: Filter.Group,
        value: selectedItems?.filter(item => item.key !== GroupFilterEnum.MyTeams).map(item => item.key) ?? [],
      });
    } else {
      // If we are selecting other groups and MyTeams is not selected, we can just update the filter
      onChange({ name: Filter.Group, value: selectedItems?.map(item => item.key) ?? [] });
    }
  };

  const options = useMemo(() => [ALL_TEAMS_OPTION, ...groups.map(getTeamPickerListItem)], [groups]);

  const selectedGroups = useMemo(
    () =>
      filterValues?.map(groupId => {
        if (groupId === GroupFilterEnum.MyTeams) {
          return ALL_TEAMS_OPTION;
        }

        const group = groups.find(group => group.id === groupId);

        return group
          ? {
              key: group.id,
              name: group.name,
            }
          : createRemovedTeamOption(groupId);
      }),
    [groups, filterValues]
  );

  return (
    <Picker
      type={multiple ? "multi" : "single"}
      id="group-filter"
      options={options}
      onSelect={handleChange}
      placeholder="Search…"
      selected={selectedGroups}
      openedOnInit
      hideClearButton
      clearSearchAfterSelection
    />
  );
};

const ALL_TEAMS_OPTION: IPickerListItem = {
  key: GroupFilterEnum.MyTeams,
  name: "My teams",
};

export default withFilterBase(GroupFilter);
